import { readFileSync, writeFileSync, unlinkSync, existsSync, mkdirSync } from 'node:fs';
import { resolve, dirname } from 'node:path';
import { homedir } from 'node:os';
import type { LicensablePlan } from './keys.js';
import { Plan } from '../billing/entitlements.js';

export interface LicenseRecord {
  key: string;
  plan: LicensablePlan;
  activatedAt: string;
  lastVerifiedAt: string;
}

const VALID_PLANS = new Set<string>([Plan.PRO, Plan.TEAM]);

/** Runtime check that parsed JSON is a valid LicenseRecord shape. */
function isLicenseRecord(value: unknown): value is LicenseRecord {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.key === 'string' &&
    typeof obj.plan === 'string' &&
    VALID_PLANS.has(obj.plan) &&
    typeof obj.activatedAt === 'string' &&
    typeof obj.lastVerifiedAt === 'string'
  );
}

function getLicensePath(bfDir?: string): string {
  const dir = bfDir ?? resolve(homedir(), '.browserforge');
  return resolve(dir, 'license.json');
}

/** Load the stored license record, or null if none/invalid. */
export function loadLicense(bfDir?: string): LicenseRecord | null {
  const path = getLicensePath(bfDir);
  if (!existsSync(path)) return null;
  try {
    const raw = readFileSync(path, 'utf-8');
    const parsed: unknown = JSON.parse(raw);
    if (!isLicenseRecord(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Persist a license record to disk with restrictive permissions. */
export function saveLicense(record: LicenseRecord, bfDir?: string): void {
  const path = getLicensePath(bfDir);
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, JSON.stringify(record, null, 2) + '\n', { encoding: 'utf-8', mode: 0o600 });
}

/** Remove the license record from disk. */
export function deleteLicense(bfDir?: string): void {
  const path = getLicensePath(bfDir);
  if (existsSync(path)) {
    unlinkSync(path);
  }
}
